import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import WalletLogo from "../../../Components/WalletLogo";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { Eye, EyeOff, Copy, Check } from "lucide-react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import SignUpConflictDialog, { getSignupConflict } from "../SignUpConflictDialog";
import {
  SIGNUP_AUTOCOMPLETE,
  signupSensitiveInputProps,
} from "../../../Utils/signupFormAutofill";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { resolveUserRoleLabel } from "../../../Utils/resolveUserRoleLabel";
import { getDefaultHomePath } from "../../../Utils/defaultHomePath";

const SpinSVG = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeOpacity=".3" strokeWidth="2.5" fill="none" />
    <path d="M7.5 2A5.5 5.5 0 0 1 13 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

function ClientSignup({
  appName = "Wallet Management",
  description,
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [clientName, setClientName] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode] = useState({
    name: "India",
    code: "IN",
    phone_code: "+91",
  });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState("");

  const [showConflictModal, setShowConflictModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const navigate = useNavigate();
  const { login, setAccess } = useAuthStore();

  useEffect(() => {
    setEmail("");
    setPassword("");
  }, []);

  useEffect(() => {
    document.body.style.overflow = showConflictModal.show || credentials ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showConflictModal.show, credentials]);

  const validateForm = () => {
    const formErrors = {};
    let isValid = true;

    if (!fullName.trim()) {
      formErrors.fullName = "Full Name is required";
      isValid = false;
    }

    if (!clientName.trim()) {
      formErrors.clientName = "Client / company name is required";
      isValid = false;
    }

    if (!email) {
      formErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      formErrors.password = "Password is required";
      isValid = false;
    } else if (password.length < 8) {
      formErrors.password = "Password must be at least 8 characters";
      isValid = false;
    }

    let nationalPhone = phone;
    if (!phone) {
      formErrors.phone = "Phone number is required";
      isValid = false;
    } else {
      const parsed = parsePhoneNumberFromString(phone, countryCode?.code || "IN");
      if (!parsed || !parsed.isValid()) {
        formErrors.phone = "Enter a valid phone number";
        isValid = false;
      } else {
        nationalPhone = parsed.nationalNumber;
        setPhone(nationalPhone);
      }
    }

    if (!agree) {
      formErrors.agree = "You must agree to the Terms of Service";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid ? { nationalPhone } : null;
  };

  const finishLogin = (user, token) => {
    localStorage.setItem("token", token);
    const allOrgs = Array.isArray(user?.organization) ? user.organization : [];
    const activeOrg =
      allOrgs.find(
        (item) =>
          Number(item.organization_id) ===
          Number(user?.user_active_organization?.organization_id)
      ) ||
      allOrgs[0] ||
      null;

    login({
      ...user,
      organizations: allOrgs,
      organization: activeOrg,
    });
    setAccess(
      resolveUserRoleLabel({
        ...user,
        organizations: allOrgs,
        organization: activeOrg,
      })
    );
  };

  const copyValue = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 1500);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  const continueAfterCredentials = () => {
    setCredentials(null);
    toast.success("Account created. You're logged in!");
    navigate(getDefaultHomePath(), { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valid = validateForm();
    if (!valid) return;
    setLoading(true);
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        full_name: fullName.trim(),
        hash_password: password,
        phone: valid.nationalPhone,
        country_phone_code: countryCode?.phone_code || "+91",
        client_name: clientName.trim(),
      };

      const res = await axios.post(
        `${MAIN_URL}/api/auth/client-signup`,
        payload,
        { headers: getApiHeaders() }
      );

      const { token, user, wallet_client: walletClient } = res.data || {};
      if (!token || !user) {
        toast.error("Signup succeeded but login failed. Please sign in.");
        navigate("/login");
        return;
      }

      finishLogin(user, token);

      if (walletClient?.api_key || walletClient?.api_secret) {
        setCredentials(walletClient);
      } else {
        toast.success("Account created. You're logged in!");
        navigate(getDefaultHomePath(), { replace: true });
      }
    } catch (error) {
      const conflict = getSignupConflict(error);
      if (conflict) {
        setShowConflictModal({
          show: true,
          title: conflict.title,
          message: conflict.message,
        });
        setErrors((prev) => ({
          ...prev,
          [conflict.type]: conflict.fieldError,
        }));
        toast.error(
          conflict.type === "email"
            ? "Email is already registered."
            : "Phone number is already registered."
        );
        return;
      }

      if (error.response?.status === 409) {
        const msg = error.response?.data?.message || "Already exists.";
        if (/client/i.test(msg)) {
          setErrors((prev) => ({ ...prev, clientName: msg }));
        }
        toast.error(msg);
        return;
      }

      if (error.response?.data) {
        const { errors: apiErrors, message } = error.response.data;
        if (apiErrors) {
          const mapped = {};
          if (apiErrors.full_name) mapped.fullName = apiErrors.full_name[0];
          if (apiErrors.email) mapped.email = apiErrors.email[0];
          if (apiErrors.hash_password) mapped.password = apiErrors.hash_password[0];
          if (apiErrors.phone) mapped.phone = apiErrors.phone[0];
          if (apiErrors.client_name) mapped.clientName = apiErrors.client_name[0];
          setErrors((prev) => ({ ...prev, ...mapped }));
        } else {
          toast.error(message || "Registration failed.");
        }
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ems-auth-root">
      <div className="ems-auth-left">
        <div className="ems-auth-card">
          <div className="ems-auth-brand">
            <WalletLogo className="ems-auth-brand-logo" />
          </div>

          <h1 className="ems-auth-h">Client Signup</h1>
          <p className="ems-auth-sub">
            Create your portal account and wallet client in one step.
          </p>

          <form
            onSubmit={handleSubmit}
            noValidate
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target?.tagName !== "BUTTON") e.preventDefault();
            }}
            {...SIGNUP_AUTOCOMPLETE.form}
          >
            <div className="ems-auth-field">
              <label htmlFor="client-signup-name">Full Name</label>
              <input
                id="client-signup-name"
                type="text"
                className="ems-fm-input"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  setErrors((p) => ({ ...p, fullName: "" }));
                }}
                {...SIGNUP_AUTOCOMPLETE.fullName}
              />
              {errors.fullName && <span className="ems-fm-err">{errors.fullName}</span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="client-signup-client">Client / Company Name</label>
              <input
                id="client-signup-client"
                type="text"
                className="ems-fm-input"
                placeholder="Acme Ticketing"
                value={clientName}
                onChange={(e) => {
                  setClientName(e.target.value);
                  setErrors((p) => ({ ...p, clientName: "" }));
                }}
                autoComplete="organization"
                name="signup-client-name"
              />
              {errors.clientName
                ? <span className="ems-fm-err">{errors.clientName}</span>
                : <span style={{ fontSize: 12, color: "var(--ems-faint)", fontWeight: 500 }}>
                    Used as your wallet client name.
                  </span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="client-signup-email">Email Address</label>
              <input
                id="client-signup-email"
                type="email"
                className="ems-fm-input"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((p) => ({ ...p, email: "" }));
                }}
                {...SIGNUP_AUTOCOMPLETE.email}
                {...signupSensitiveInputProps}
              />
              {errors.email && <span className="ems-fm-err">{errors.email}</span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="client-signup-pw">Password</label>
              <div className="ems-auth-pw">
                <input
                  id="client-signup-pw"
                  type={showPassword ? "text" : "password"}
                  className="ems-fm-input"
                  placeholder="Create a secure password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((p) => ({ ...p, password: "" }));
                  }}
                  {...SIGNUP_AUTOCOMPLETE.password}
                  {...signupSensitiveInputProps}
                />
                <button
                  type="button"
                  className="ems-auth-pw-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password
                ? <span className="ems-fm-err">{errors.password}</span>
                : <span style={{ fontSize: 12, color: "var(--ems-faint)", fontWeight: 500 }}>
                    Minimum 8 characters.
                  </span>}
            </div>

            <div className="ems-auth-field">
              <label>Phone Number</label>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <input
                  type="text"
                  value={countryCode?.phone_code || "+91"}
                  readOnly
                  aria-label="Country code"
                  className="ems-fm-input"
                  style={{ height: 42, width: 72, flexShrink: 0, textAlign: "center" }}
                />
                <input
                  type="tel"
                  placeholder="Enter Phone Number"
                  value={phone}
                  style={{ height: 42 }}
                  className="ems-fm-input"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d{0,15}$/.test(val)) {
                      setPhone(val);
                      setErrors((p) => ({ ...p, phone: "" }));
                    }
                  }}
                  {...SIGNUP_AUTOCOMPLETE.phone}
                />
              </div>
              {errors.phone && <span className="ems-fm-err">{errors.phone}</span>}
            </div>

            <div className="ems-auth-meta" style={{ marginBottom: 24 }}>
              <label className="ems-auth-remember">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => {
                    setAgree(e.target.checked);
                    setErrors((p) => ({ ...p, agree: "" }));
                  }}
                />
                I agree to the{" "}
                <Link to="/terms" style={{ color: "var(--ems-primary)", fontWeight: 650 }}>
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" style={{ color: "var(--ems-primary)", fontWeight: 650 }}>
                  Privacy Policy
                </Link>
                .
              </label>
            </div>
            {errors.agree && (
              <span className="ems-fm-err" style={{ display: "block", marginBottom: 12, marginTop: -16 }}>
                {errors.agree}
              </span>
            )}

            <div className="ems-auth-actions">
              <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                {loading ? <><SpinSVG /> Creating account…</> : "Create Client Account"}
              </button>
            </div>

            <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "var(--ems-muted)", fontWeight: 500 }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: "var(--ems-primary)", fontWeight: 650, textDecoration: "none" }}>
                Login
              </Link>
            </p>
          </form>
        </div>
      </div>

      <div className="ems-auth-right">
        <div className="ems-auth-hero">
          <div className="ems-auth-hero-mark">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <path d="M5 8h16M5 13h10M5 18h16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="ems-auth-hero-h">{appName}</h2>
          <p className="ems-auth-hero-p">
            {description ||
              "Sign up as a wallet client to manage agents, transactions, and credit from your portal."}
          </p>
     
        </div>
        <p className="ems-auth-copy">
          &copy; {new Date().getFullYear()} {appName}. All rights reserved.
        </p>
      </div>

      {credentials && (
        <div className="ems-org-overlay" role="dialog" aria-modal="true">
          <div className="ems-org-picker" style={{ maxWidth: 480 }}>
            <div className="ems-org-picker-hd">
              <WalletLogo className="ems-auth-brand-logo" />
              <h3 className="ems-org-picker-h">Save your API credentials</h3>
              <p className="ems-org-picker-sub">
                Your wallet client <strong>{credentials.client_name}</strong> was created.
                The API secret is shown only once — copy it now.
              </p>
            </div>

            <div style={{ display: "grid", gap: 12, padding: "0 4px 8px" }}>
              {[
                { key: "api_key", label: "API Key", value: credentials.api_key },
                { key: "api_secret", label: "API Secret", value: credentials.api_secret },
              ].map((row) => (
                <div key={row.key}>
                  <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 4, color: "var(--ems-muted)" }}>
                    {row.label}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <code
                      style={{
                        flex: 1,
                        fontSize: 12,
                        padding: "10px 12px",
                        background: "var(--ems-surface, #f4f5f7)",
                        borderRadius: 8,
                        wordBreak: "break-all",
                      }}
                    >
                      {row.value || "—"}
                    </code>
                    <button
                      type="button"
                      className="ems-btn ems-ghost"
                      onClick={() => copyValue(row.key, row.value)}
                      aria-label={`Copy ${row.label}`}
                      disabled={!row.value}
                      style={{ minWidth: 44, padding: "8px 10px" }}
                    >
                      {copiedField === row.key ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="ems-org-foot">
              <button type="button" className="ems-btn ems-primary" onClick={continueAfterCredentials}>
                Continue to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      <SignUpConflictDialog
        open={showConflictModal.show}
        title={showConflictModal.title}
        message={showConflictModal.message}
        onClose={() => setShowConflictModal((prev) => ({ ...prev, show: false }))}
        onLogin={() => {
          setShowConflictModal((prev) => ({ ...prev, show: false }));
          navigate("/login");
        }}
      />
    </div>
  );
}

export default ClientSignup;
